// Ported from dev-scrips-pm-react services/scheduling/use-patient-search.ts (PROD-1190 calendar port)
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * GET /api/Patients/GetPatientList?OrganizationId=&PageNumber=&PageSize=&Search=
 * Mirrors Flutter `FetchPatientsUseCase` →
 *   patients_list_data_source_impl.dart:34
 *
 * Response shape per Flutter `PatientsList`:
 *   { currentPage, totalPage, count, patientData: PatientDatum[] }
 *
 * Each entry uses `patientId` (not `id`), and may carry either a combined
 * `name` or split `firstName/lastName`. We normalize both to the React
 * `PatientSearchResult` shape consumed by the typeahead UI.
 */

interface PatientDatum {
  patientId?: string;
  id?: string;
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  mrn?: string;
  birthDate?: string;
  dob?: string;
  age?: string;
  /** Backend ships `gender` as either a string or `{ code, display }`. */
  gender?: string | { code?: string; display?: string };
  phone?: string;
  contactNumber?: string;
  email?: string;
  image?: string;
  photo?: string;
  photoUrl?: string;
  importance?: { code?: string; display?: string };
  isMissingData?: boolean;
}

export interface PatientImportance {
  code?: string;
  display?: string;
}

export interface PatientSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  mrn?: string;
  dateOfBirth?: string;
  birthDate?: string;
  age?: string;
  gender?: string;
  phone?: string;
  photo?: string;
  photoUrl?: string;
  importance?: PatientImportance;
  /** Backend flag: patient profile has incomplete demographics. Drives the
   *  yellow warning icon next to the name (Flutter `patient_widget.dart:104`). */
  isMissingData?: boolean;
}

interface PatientListResponse {
  patientData?: PatientDatum[];
  patientList?: PatientDatum[];
  patients?: PatientDatum[];
}

interface UsePatientSearchParams {
  organizationId: string;
  query: string;
  pageSize?: number;
}

function splitName(name: string | undefined): { firstName: string; lastName: string } {
  if (!name) return { firstName: '', lastName: '' };
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function normalize(d: PatientDatum): PatientSearchResult {
  const fallback = splitName(d.name);
  const firstName = d.firstName ?? fallback.firstName;
  const lastName = d.lastName ?? fallback.lastName;
  const gender =
    typeof d.gender === 'string' ? d.gender : d.gender?.display ?? d.gender?.code;
  return {
    id: d.patientId ?? d.id ?? '',
    firstName,
    lastName,
    middleName: d.middleName,
    mrn: d.mrn,
    dateOfBirth: d.dob,
    birthDate: d.birthDate,
    age: d.age,
    gender: gender || undefined,
    phone: d.phone ?? d.contactNumber,
    photo: d.image ?? d.photo,
    photoUrl: d.photoUrl,
    importance: d.importance,
    isMissingData: d.isMissingData,
  };
}

export function usePatientSearch({ organizationId, query, pageSize = 20 }: UsePatientSearchParams) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['patient-search', organizationId, trimmed, pageSize],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({
        OrganizationId: organizationId,
        PageNumber: '0',
        PageSize: String(pageSize),
      });
      if (trimmed.length > 0) qs.set('Search', trimmed);
      const res = await get<PatientListResponse | PatientDatum[]>(
        `/api/Patients/GetPatientList?${qs.toString()}`,
        { signal },
      );
      const list: PatientDatum[] = Array.isArray(res)
        ? res
        : res?.patientData ?? res?.patientList ?? res?.patients ?? [];
      return list.map(normalize).filter((p) => p.id);
    },
    enabled: !!organizationId && trimmed.length >= 2,
    staleTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
}
